import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './ModifierLoge.css';

interface DateItem {
  id: string;
  date: string;
}

interface Loge {
  nom: string;
  orient: string;
  date: string;
  jour: string;
  image: string;
  presentation: string;
  adresseTemple: string;
}

function ModifierLoge() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loge, setLoge] = useState<Loge | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dates, setDates] = useState<DateItem[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  // ✅ droits
  const [isAdmin, setIsAdmin] = useState(false);
  const [myFonctionsParLoge, setMyFonctionsParLoge] = useState<Record<string, string>>({});
  const [canEditUser, setCanEditUser] = useState(false);

  const jours = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

  // ====== Charger utilisateur connecté (admin + fonctionsParLoge) ======
  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    const unsub = onAuthStateChanged(auth, async (user) => {
      try {
        if (!user?.email) return;

        const q = query(collection(db, 'Membres'), where('mail', '==', user.email));
        const snap = await getDocs(q);

        if (!snap.empty) {
          const me = snap.docs[0].data() as any;
          const admin = me.administrateur === 'oui';
          setIsAdmin(admin);

          const fp = (me.fonctionsParLoge || {}) as Record<string, string>;
          setMyFonctionsParLoge(fp);

          // base (sera recalculé quand la loge est chargée)
          setCanEditUser(admin);
        }
      } catch (e) {
        console.error(e);
      }
    });

    return () => unsub();
  }, []);

  // ====== Charger les dates ======
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const db = getFirestore();
        const datesSnapshot = await getDocs(collection(db, 'Date'));
        const datesData = datesSnapshot.docs.map((d) => ({
          id: d.id,
          date: (d.data() as any).date || d.id
        }));
        setDates(datesData);
      } catch (err) {
        console.error('Erreur lors du chargement des dates:', err);
      }
    };

    fetchDates();
  }, []);

  // ====== Charger la loge ======
  useEffect(() => {
    const fetchLoge = async () => {
      if (!id) {
        setError('Identifiant de la loge manquant');
        setLoading(false);
        return;
      }

      try {
        const db = getFirestore();
        const logeDoc = await getDoc(doc(db, 'Loges', id));

        if (logeDoc.exists()) {
          const logeData = logeDoc.data() as Loge;
          setLoge(logeData);

          if (logeData.image) setImagePreview(logeData.image);
        } else {
          setError('Loge non trouvée');
        }
      } catch (err) {
        console.error('Erreur lors de la récupération:', err);
        setError('Erreur lors de la récupération de la loge');
      } finally {
        setLoading(false);
      }
    };

    fetchLoge();
  }, [id]);

  // ====== Recalcul des droits quand loge chargée ======
  useEffect(() => {
    if (!loge) return;

    if (isAdmin) {
      setCanEditUser(true);
      return;
    }

    // 👉 règle: Vénérable / Secrétaire de CETTE loge (nom)
    const role = myFonctionsParLoge?.[loge.nom];
    setCanEditUser(role === 'Vénérable' || role === 'Secrétaire');
  }, [loge, isAdmin, myFonctionsParLoge]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditUser) return;

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          setImagePreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (): Promise<string> => {
    if (!imageFile) return loge?.image || '';

    setUploading(true);
    try {
      const storage = getStorage();
      const timestamp = Date.now();
      const imageRef = ref(storage, `loges/${timestamp}_${imageFile.name}`);

      await uploadBytes(imageRef, imageFile);
      return await getDownloadURL(imageRef);
    } catch (err) {
      console.error("Erreur lors du téléchargement de l'image:", err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id || !loge) return;

    if (!canEditUser) {
      setError("Vous n'avez pas les droits pour modifier cette loge.");
      return;
    }

    try {
      setError('');
      let updatedLoge = { ...loge };

      if (imageFile) {
        const imageUrl = await uploadImage();
        updatedLoge.image = imageUrl;
      }

      const db = getFirestore();
      await updateDoc(doc(db, 'Loges', id), updatedLoge);

      // 👉 retour logique: si non-admin => retour Ma Loge, sinon page loges
      navigate(isAdmin ? '/mapage/loges' : '/mapage/ma-loge');
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError('Erreur lors de la mise à jour de la loge');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (!canEditUser) return;

    const { name, value } = e.target;
    setLoge((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  if (loading) return <div>Chargement...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!loge) return <div>Loge non trouvée</div>;

  return (
    <div className="modifier-loge-container">
      <h2>Modifier la loge</h2>

      {!canEditUser && (
        <div className="error-message">
          Vous n’avez pas les droits de modification pour cette loge.
        </div>
      )}

      <form onSubmit={handleSubmit} className="modifier-loge-form">
        <div className="form-group">
          <label>Nom de la Loge:</label>
          <input
            type="text"
            name="nom"
            value={loge.nom}
            onChange={handleChange}
            className="form-input"
            disabled
          />
        </div>

        <div className="form-group">
          <label>Orient:</label>
          <input
            type="text"
            name="orient"
            value={loge.orient}
            onChange={handleChange}
            required
            className="form-input"
            disabled={!canEditUser}
          />
        </div>

        <div className="date-container">
          <div className="form-group">
            <label>Date de Tenue:</label>
            <select
              name="date"
              value={loge.date}
              onChange={handleChange}
              required
              className="form-select"
              disabled={!canEditUser}
            >
              <option value="">Sélectionner une date</option>
              {dates.map((date) => (
                <option key={date.id} value={date.id}>
                  {date.date}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Jour de Tenue:</label>
            <select
              name="jour"
              value={loge.jour}
              onChange={handleChange}
              required
              className="form-select"
              disabled={!canEditUser}
            >
              <option value="">Sélectionner un jour</option>
              {jours.map((jour) => (
                <option key={jour} value={jour}>
                  {jour}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Image de la Loge:</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="form-input"
            disabled={!canEditUser}
          />
          <small className="form-help">
            Sélectionnez une nouvelle image pour remplacer l'existante (formats acceptés: jpg, png, gif)
          </small>
        </div>

        {imagePreview && (
          <div className="image-preview">
            <img
              src={imagePreview}
              alt="Aperçu"
              onError={(e) => {
                (e.target as HTMLImageElement).alt = 'Image invalide';
              }}
            />
          </div>
        )}

        <div className="form-group">
          <label>Présentation:</label>
          <textarea
            name="presentation"
            value={loge.presentation}
            onChange={handleChange}
            className="form-textarea"
            rows={10}
            placeholder="Entrez la présentation de la loge..."
            disabled={!canEditUser}
          />
        </div>

        <div className="form-group">
          <label>Adresse Temple:</label>
          <textarea
            name="adresseTemple"
            value={loge.adresseTemple || ''}
            onChange={handleChange}
            className="form-textarea"
            rows={3}
            placeholder="Entrez l'adresse du temple..."
            disabled={!canEditUser}
          />
        </div>

        <div className="buttons-container">
          <button type="submit" className="save-btn" disabled={uploading || !canEditUser}>
            {uploading ? 'Enregistrement en cours...' : 'Enregistrer'}
          </button>
          <button
            type="button"
            className="cancel-btn"
            onClick={() => navigate(isAdmin ? '/mapage/loges' : '/mapage/ma-loge')}
            disabled={uploading}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}

export default ModifierLoge;
